import React, { useState } from 'react';
import TextField from '@mui/material/TextField';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Card,
  CardMedia,
} from '@mui/material';
import { DropzoneArea } from 'react-mui-dropzone';
import { useNavigate, useParams } from 'react-router-dom';
import makeStyles from '@mui/styles/makeStyles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import moment from 'moment';
import { useCatch } from '../reactHelper';
import EditItemView from '../settings/components/EditItemView';
import { useTranslation } from '../common/components/LocalizationProvider';
import PoiMenu from './components/PoiMenu';
import EditAttributesAccordion from '../settings/components/EditAttributesAccordion';
import LinkPositionalField from '../common/components/LinkPositionalField';
import PositionalTable from './components/PositionalTable';
import { removeSeconds } from '../common/util/converter';
import LinkField from '../common/components/LinkField';
import useEventAttributes from '../common/attributes/useEventAttributes';

const useStyles = makeStyles((theme) => ({
  details: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    paddingBottom: theme.spacing(3),
  },
}));

const GuideEventPage = () => {
  const classes = useStyles();
  const t = useTranslation();
  const eventAttributes = useEventAttributes(t);

  const [item, setItem] = useState();
  const [contents, setContents] = useState([]);
  const { id } = useParams();
  const navigate = useNavigate();

  const addContent = (value) => {
    const contTemp = [...contents];
    contTemp.push(value);
    setContents(contTemp);
  };

  const handleSave = useCatch(async () => {
    let url = '/api/guideevents';

    const itemCopy = structuredClone(item);

    if (!itemCopy.datestart) itemCopy.datestart = moment().locale('en').format(moment.HTML5_FMT.DATETIME_LOCAL);
    if (!itemCopy.dateend) itemCopy.dateend = moment().add(1, 'hour').locale('en').format(moment.HTML5_FMT.DATETIME_LOCAL);

    if (id) {
      url += `/${id}`;
    }

    const response = await fetch(url, {
      method: !id ? 'POST' : 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(itemCopy),
    });

    if (response.ok) {
      if (!id) {
        navigate(-1);
        return;
      }
      let bodyContent = `guideeventid=${item.id}`;
      contents.forEach((content) => {
        bodyContent += `&contents=${content.id}`;
      });
      const res = await fetch('/api/guideevents/contents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: bodyContent,
      });
      if (res.ok) {
        navigate(-1);
      }
    } else {
      throw Error(await response.text());
    }
  });

  const handleFiles = useCatch(async (files) => {
    if (files.length > 0) {
      const response = await fetch(`/api/guideevents/${item.id}/image`, {
        method: 'POST',
        body: files[0],
      });
      if (response.ok) {
        setItem({ ...item, attributes: { ...item.attributes, image: await response.text() } });
      } else {
        throw Error(await response.text());
      }
    }
  });

  const validate = () => item && item.name;

  return (
    <EditItemView
      endpoint="guideevents"
      item={item}
      setItem={setItem}
      validate={validate}
      menu={<PoiMenu />}
      customHandle={handleSave}
      customText="sharedSave"
      getContents
      setContents={setContents}
      breadcrumbs={['managePoi']}
    >
      {item && (
        <>
          <Accordion defaultExpanded style={{ borderRadius: 20 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">
                {t('sharedRequired')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <TextField
                value={item.name || ''}
                onChange={(event) => setItem({ ...item, name: event.target.value })}
                label={t('sharedName')}
              />
            </AccordionDetails>
            <AccordionDetails className={classes.details}>
              <TextField
                label={t('reportFrom')}
                type="datetime-local"
                value={removeSeconds(item.datestart) || moment().locale('en').format(moment.HTML5_FMT.DATETIME_LOCAL)}
                onChange={(e) => setItem({ ...item, datestart: e.target.value })}
                fullWidth
              />
            </AccordionDetails>
            <AccordionDetails className={classes.details}>
              <TextField
                label={t('reportTo')}
                type="datetime-local"
                value={removeSeconds(item.dateend) || moment().add(1, 'hour').locale('en').format(moment.HTML5_FMT.DATETIME_LOCAL)}
                onChange={(e) => setItem({ ...item, dateend: e.target.value })}
                fullWidth
              />
            </AccordionDetails>
          </Accordion>
          {item.id && (
            <>
              {item.attributes && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1">
                      {t('positionImage')}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.details}>
                    <Card elevation={3}>
                      <CardMedia image={`/api/media/public/images/guideevents/${item.id}/${item.attributes.image}?dummy=${Math.floor(Math.random() * 1000)}`} style={{ height: 200 }} />
                    </Card>
                    <DropzoneArea
                      dropzoneText={t('sharedDropzoneText')}
                      acceptedFiles={['image/*']}
                      filesLimit={1}
                      onChange={handleFiles}
                      showAlerts={false}
                    />
                  </AccordionDetails>
                </Accordion>
              )}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">
                    {t('content')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails className={classes.details}>
                  <LinkPositionalField endpoint="/api/contents" add={addContent} label={t('contents')} />
                  <PositionalTable items={contents} setItems={setContents} />
                </AccordionDetails>
              </Accordion>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">
                    {t('sharedConnections')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails className={classes.details}>
                  <LinkField
                    endpointAll="/api/ticketitems"
                    endpointLinked={`/api/ticketitems?guideeventid=${item.id}`}
                    baseId={item.id}
                    keyBase="guideeventId"
                    keyLink="ticketitemId"
                    label={t('ticket')}
                  />
                </AccordionDetails>
              </Accordion>
            </>
          )}
          <EditAttributesAccordion
            attributes={item.attributes}
            setAttributes={(attributes) => setItem({ ...item, attributes })}
            definitions={eventAttributes}
          />
        </>
      )}
    </EditItemView>
  );
};

export default GuideEventPage;
